import React, { useContext, useState } from 'react';
import { AudioSurah } from '@/infrastructure/class/audio-surah';
import { findNextObject } from '@/utils/helpers/find-next-object';
import { findPreviousObject } from '@/utils/helpers/find-previous-object';
import { findAndInsert } from '@/utils/helpers/find-and-insert';
import { getRandomElement } from '@/utils/helpers/get-random-element';

export interface IPlayerContext {
  player: IPlayerState;
  setTracks: (tracks: AudioSurah[], currentTrack: AudioSurah) => void;
  nextTrack: () => void;
  prevTrack: () => void;
  playNext: (nextTrack: AudioSurah) => void;
  setRandomTrack: () => void;
  shuffleTracks: (tracks: AudioSurah[]) => void;
  setPlaying: (playing: boolean) => void;
  setEnded: (ended: boolean) => void;
  togglePlayer: () => void;
}

interface IPlayerState {
  opened: boolean;
  playing: boolean;
  ended: boolean;
  currentTrack: AudioSurah | null;
  tracks: AudioSurah[];
}

export const PlayerContext = React.createContext<IPlayerContext | null>(null);

interface IPlayerProvider {
  children?: React.ReactNode;
}

const initialState: IPlayerState = {
  opened: false,
  playing: false,
  ended: false,
  currentTrack: null,
  tracks: [],
};

const PlayerProvider: React.FC<IPlayerProvider> = ({ children }) => {
  const [player, setPlayer] = useState<IPlayerState>(initialState);

  const setTracks = (tracks: AudioSurah[], currentTrack: AudioSurah) => {
    setPlayer({
      ...player,
      opened: true,
      playing: true,
      tracks,
      currentTrack,
    });
  };

  const nextTrack = () => {
    let currentTrack = null;
    if (player.currentTrack) {
      currentTrack = findNextObject<AudioSurah>(
        player.tracks,
        player.currentTrack,
        'id'
      );
    }

    setPlayer({
      ...player,
      currentTrack,
    });
  };

  const prevTrack = () => {
    let currentTrack = null;
    if (player.currentTrack) {
      currentTrack = findPreviousObject<AudioSurah>(
        player.tracks,
        player.currentTrack,
        'id'
      );
    }

    setPlayer({
      ...player,
      currentTrack,
    });
  };

  const playNext = (nextTrack: AudioSurah) => {
    let tracks = [nextTrack];
    let currentTrack = nextTrack;
    if (player.currentTrack) {
      tracks = findAndInsert(
        player.tracks,
        player.currentTrack,
        nextTrack,
        'id'
      );
      currentTrack = player.currentTrack;
    }

    setPlayer({
      ...player,
      tracks,
      currentTrack,
      opened: true,
      playing: true,
    });
  };

  const setRandomTrack = () => {
    let currentTrack = null;
    if (player.currentTrack) {
      currentTrack = getRandomElement<AudioSurah>(player.tracks);
    }

    setPlayer({
      ...player,
      currentTrack,
    });
  };

  const shuffleTracks = (tracks: AudioSurah[]) => {
    const currentTrack = getRandomElement<AudioSurah>(tracks);

    setPlayer({
      ...player,
      currentTrack,
      tracks,
      opened: true,
      playing: true,
    });
  };

  const setPlaying = (playing: boolean) => {
    setPlayer({
      ...player,
      playing,
    });
  };

  const setEnded = (ended: boolean) => {
    setPlayer({
      ...player,
      ended,
    });
  };

  const togglePlayer = () => {
    setPlayer({
      ...player,
      opened: !player.opened,
    });
  };

  const value = {
    player,
    setTracks,
    nextTrack,
    prevTrack,
    playNext,
    setRandomTrack,
    shuffleTracks,
    setPlaying,
    setEnded,
    togglePlayer,
  };

  return (
    <PlayerContext.Provider value={value}>{children}</PlayerContext.Provider>
  );
};

export const usePlayer = () => useContext(PlayerContext) as IPlayerContext;

export default PlayerProvider;
